import React, { useState, useEffect, useContext, useRef } from "react";
import clsx from "clsx";
import { useHistory } from "react-router-dom";
import { AEROS, mixHex } from "../../styles/aerosTokens";
import {
  makeStyles,
  Paper,
  InputBase,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Switch,
} from "@material-ui/core";
import {
  MoveToInbox as MoveToInboxIcon,
  CheckBox as CheckBoxIcon,
  Search as SearchIcon,
  Add as AddIcon,
  TextRotateUp,
  TextRotationDown,
} from "@material-ui/icons";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import ToggleButton from "@material-ui/lab/ToggleButton";

import { FilterAlt, PlaylistAddCheckOutlined } from "@mui/icons-material";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsListCustom";
import TabPanel from "../TabPanel";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import { TagsFilter } from "../TagsFilter";
import { UsersFilter } from "../UsersFilter";
import { StatusFilter } from "../StatusFilter";
import { WhatsappsFilter } from "../WhatsappsFilter";
import { Button, Snackbar } from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { QueueSelectedContext } from "../../context/QueuesSelected/QueuesSelectedContext";

import api from "../../services/api";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    ticketsWrapper: {
      position: "relative",
      display: "flex",
      height: "100%",
      flexDirection: "column",
      overflow: "hidden",
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },

    // Topo do painel (busca + ferramentas), padding do mockup: 16px 16px 0
    panelHeader: {
      flex: "none",
      padding: "16px 16px 0",
    },

    // Campo de busca: surface, border 1px, radius 10, padding 9px 14px, 13px
    searchField: {
      background: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.aeros.border : "rgba(255,255,255,0.1)"}`,
      borderRadius: 10,
      padding: "9px 14px",
      display: "flex",
      alignItems: "center",
      gap: 8,
    },

    searchIcon: {
      color: theme.palette.text.secondary,
      fontSize: 18,
      flex: "none",
    },

    searchInput: {
      flex: 1,
      fontSize: 13,
      color: theme.palette.text.primary,
      "& input": {
        padding: 0,
      },
      "& input::placeholder": {
        color: theme.palette.text.secondary,
        opacity: 1,
      },
    },

    filterButton: {
      padding: 0,
      flex: "none",
      backgroundColor: "transparent",
      "&:hover": {
        backgroundColor: "transparent",
      },
    },

    filterIcon: {
      fontSize: 18,
      color: theme.palette.aeros.textTertiary,
    },

    filterIconActive: {
      color: AEROS.cyan,
    },

    searchOnMessagesRow: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      padding: "8px 16px 0",
      fontSize: 12,
      color: theme.palette.text.secondary,
    },

    // Linha de ferramentas do mockup: gap 6, margin-top 12
    toolsRow: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      marginTop: 12,
    },

    queueSelectWrapper: {
      marginLeft: "auto",
    },

    // Abas Aeros: margin-top 14, border-bottom 1px no container
    aerosTabs: {
      margin: "14px 0 0",
      minHeight: 0,
      flex: "none",
      borderBottom: `1px solid ${isLight ? theme.palette.aeros.border : "rgba(255,255,255,0.08)"}`,
    },

    aerosTab: {
      flex: 1,
      minWidth: 0,
      maxWidth: "none",
      minHeight: 0,
      padding: "10px 0",
      fontSize: 12.5,
      fontWeight: 500,
      textTransform: "none",
      textAlign: "center",
      color: theme.palette.text.secondary,
      opacity: 1,
      borderBottom: "2px solid transparent",
      "&.Mui-selected": {
        fontWeight: 700,
        color: theme.palette.text.primary,
        borderBottom: "2px solid",
        borderImage: `linear-gradient(90deg, ${theme.palette.primary.main}, ${AEROS.cyan}) 1`,
      },
    },

    // Pill de contagem inline após o texto da aba
    countPill: {
      borderRadius: 6,
      padding: "1px 7px",
      fontSize: 11,
      fontWeight: 600,
      marginLeft: 6,
      display: "inline-block",
      lineHeight: "16px",
      verticalAlign: "baseline",
    },
    countPillActive: {
      backgroundColor: "rgba(160,68,255,0.2)",
      color: isLight ? AEROS.stateTextOnLight.purple : "#C08BFF",
    },
    countPillPending: {
      backgroundColor: "rgba(251,191,36,0.12)",
      color: isLight ? AEROS.stateTextOnLight.warning : AEROS.warning,
    },
    countPillNeutral: {
      backgroundColor: isLight ? "rgba(15,20,40,0.07)" : "rgba(255,255,255,0.07)",
    },

    show: {
      display: "block",
    },

    hide: {
      display: "none !important",
    },

    closeAllFab: {
      backgroundColor: AEROS.error,
      marginBottom: "4px",
      "&:hover": {
        backgroundColor: mixHex(AEROS.error, "#000000", 0.2),
      },
    },

    snackbar: {
      display: "flex",
      justifyContent: "space-between",
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderRadius: 30,
      [theme.breakpoints.down("sm")]: {
        fontSize: "0.8em",
      },
      [theme.breakpoints.up("md")]: {
        fontSize: "1em",
      },
    },

    yesButton: {
      backgroundColor: theme.palette.aeros.surface,
      color: AEROS.success,
      padding: "4px 4px",
      fontSize: "1em",
      fontWeight: "bold",
      textTransform: "uppercase",
      marginRight: theme.spacing(1),
      "&:hover": {
        backgroundColor: AEROS.success,
        color: "#07080D",
      },
      borderRadius: 30,
    },
    noButton: {
      backgroundColor: theme.palette.aeros.surface,
      color: AEROS.error,
      padding: "4px 4px",
      fontSize: "1em",
      fontWeight: "bold",
      textTransform: "uppercase",
      "&:hover": {
        backgroundColor: AEROS.error,
        color: "#07080D",
      },
      borderRadius: 30,
    },

    // Botões de ícone 32×32 da linha de ferramentas (mockup)
    standardButton: {
      height: 32,
      width: 32,
      border: `1px solid ${isLight ? theme.palette.aeros.border : "rgba(255,255,255,0.1)"}`,
      borderRadius: 9,
      padding: 0,
      minWidth: "auto",
      backgroundColor: "transparent",
      transition: "border-color 0.2s ease, background-color 0.2s ease",
      "&:hover": {
        borderColor: "rgba(34,211,238,0.4)",
        backgroundColor: "rgba(34,211,238,0.08)",
      },
    },
    activeButton: {
      borderColor: "rgba(34,211,238,0.4)",
      backgroundColor: "rgba(34,211,238,0.08)",
    },
    standardIcon: {
      color: theme.palette.text.secondary,
      fontSize: 17,
      "&:hover": {
        color: AEROS.cyan,
      },
    },
    activeIcon: {
      color: AEROS.cyan,
    },
  };
});

const TicketsManagerTabs = () => {
  const classes = useStyles();
  const history = useHistory();

  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [sortTickets, setSortTickets] = useState(false);

  const searchInputRef = useRef();
  const [searchOnMessages, setSearchOnMessages] = useState(true);

  const { user } = useContext(AuthContext);
  const { profile } = user;
  const { setSelectedQueuesMessage } = useContext(QueueSelectedContext);
  const { tabOpen, setTabOpen } = useContext(TicketsContext);

  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [groupingCount, setGroupingCount] = useState(0);

  const userQueueIds = user.queues.map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState([]);
  const [forceSearch, setForceSearch] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [filter, setFilter] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [isFilterActive, setIsFilterActive] = useState(false);

  useEffect(() => {
    setSelectedQueuesMessage(selectedQueueIds);
  }, [selectedQueueIds]);

  useEffect(() => {
    if (user.profile.toUpperCase() === "ADMIN" || user.allUserChat.toUpperCase() === "ENABLED") {
      setShowAllTickets(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "search") {
      searchInputRef.current.focus();
    }
    setForceSearch(!forceSearch);
  }, [tab]);

  let searchTimeout;

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();

    clearTimeout(searchTimeout);

    if (searchedTerm === "") {
      setSearchParam(searchedTerm);
      setForceSearch(!forceSearch);
      setTab("open");
      return;
    } else if (tab !== "search") {
      handleFilter();
      setTab("search");
    }

    searchTimeout = setTimeout(() => {
      setSearchParam(searchedTerm);
      setForceSearch(!forceSearch);
    }, 500);
  };

  const handleBack = () => {
    history.push("/tickets");
  };

  const handleChangeTab = (e, newValue) => {
    setTab(newValue);
  };

  const handleChangeTabOpen = (e, newValue) => {
    handleBack();
    setTabOpen(newValue);
  };

  const applyPanelStyle = (status) => {
    if (tabOpen !== status) {
      return { width: 0, height: 0 };
    }
  };

  const handleSnackbarOpen = () => {
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const CloseAllTicket = async () => {
    try {
      const { data } = await api.post("/tickets/closeAll", {
        status: tabOpen,
        selectedQueueIds,
      });
      handleSnackbarClose();
    } catch (err) {
      console.log("Error: ", err);
    }
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleSelectedTags = (selecteds) => {
    const tags = selecteds.map((t) => t.id);

    clearTimeout(searchTimeout);

    if (tags.length === 0) {
      setForceSearch(!forceSearch);
    } else if (tab !== "search") {
      setTab("search");
    }

    searchTimeout = setTimeout(() => {
      setSelectedTags(tags);
      setForceSearch(!forceSearch);
    }, 500);
  };

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);

    clearTimeout(searchTimeout);

    if (users.length === 0) {
      setForceSearch(!forceSearch);
    } else if (tab !== "search") {
      setTab("search");
    }
    searchTimeout = setTimeout(() => {
      setSelectedUsers(users);
      setForceSearch(!forceSearch);
    }, 500);
  };

  const handleSelectedWhatsapps = (selecteds) => {
    const whatsapp = selecteds.map((t) => t.id);

    clearTimeout(searchTimeout);

    if (whatsapp.length === 0) {
      setForceSearch(!forceSearch);
    } else if (tab !== "search") {
      setTab("search");
    }
    searchTimeout = setTimeout(() => {
      setSelectedWhatsapp(whatsapp);
      setForceSearch(!forceSearch);
    }, 500);
  };

  const handleSelectedStatus = (selecteds) => {
    const statusFilter = selecteds.map((t) => t.status);

    clearTimeout(searchTimeout);

    if (statusFilter.length === 0) {
      setForceSearch(!forceSearch);
    } else if (tab !== "search") {
      setTab("search");
    }

    searchTimeout = setTimeout(() => {
      setSelectedStatus(statusFilter);
      setForceSearch(!forceSearch);
    }, 500);
  };

  const handleFilter = () => {
    if (filter) {
      setFilter(false);
      setTab("open");
      setSearchParam("");
      setSelectedTags([]);
      setSelectedUsers([]);
      setSelectedWhatsapp([]);
      setSelectedStatus([]);
      setForceSearch(!forceSearch);
      if (searchInputRef.current) {
        searchInputRef.current.value = "";
      }
    } else {
      setFilter(true);
      setTab("search");
    }
  };

  const [open, setOpen] = React.useState(false);
  const [hidden, setHidden] = React.useState(false);

  const handleVisibility = () => {
    setHidden((prevHidden) => !prevHidden);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClosed = () => {
    setOpen(false);
  };

  const tooltipTitleStyle = {
    fontSize: "10px",
  };

  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />
      <div className={classes.panelHeader}>
        <div className={classes.searchField}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase
            className={classes.searchInput}
            inputRef={searchInputRef}
            placeholder={i18n.t("tickets.search.placeholder")}
            type="search"
            onChange={handleSearch}
          />
          <Tooltip placement="top" title={i18n.t("tickets.search.filterConections")}>
            <IconButton
              aria-label="filter"
              className={classes.filterButton}
              onClick={() => {
                setIsFilterActive((prevState) => !prevState);
                handleFilter();
              }}
            >
              <FilterAlt
                className={clsx(classes.filterIcon, {
                  [classes.filterIconActive]: isFilterActive,
                })}
              />
            </IconButton>
          </Tooltip>
        </div>

        <div className={classes.toolsRow}>
          <Can
            role={user.allUserChat === 'enabled' && user.profile === 'user' ? 'admin' : user.profile}
            perform="tickets-manager:showall"
            yes={() => (
              <Tooltip placement="top" title="Todos">
                <ToggleButton
                  className={clsx(classes.standardButton, {
                    [classes.activeButton]: showAllTickets,
                  })}
                  value="uncheck"
                  selected={showAllTickets}
                  onChange={() => setShowAllTickets((prevState) => !prevState)}
                >
                  {showAllTickets ? (
                    <VisibilityIcon className={clsx(classes.standardIcon, classes.activeIcon)} />
                  ) : (
                    <VisibilityOffIcon className={classes.standardIcon} />
                  )}
                </ToggleButton>
              </Tooltip>
            )}
          />
          <Snackbar
            open={snackbarOpen}
            onClose={handleSnackbarClose}
            message={i18n.t("tickets.inbox.closedAllTickets")}
            ContentProps={{
              className: classes.snackbar,
            }}
            action={
              <>
                <Button
                  className={classes.yesButton}
                  size="small"
                  onClick={CloseAllTicket}
                >
                  {i18n.t("tickets.inbox.yes")}
                </Button>
                <Button
                  className={classes.noButton}
                  size="small"
                  onClick={handleSnackbarClose}
                >
                  {i18n.t("tickets.inbox.no")}
                </Button>
              </>
            }
          />
          <Tooltip placement="top" title={i18n.t("tickets.inbox.newTicket")}>
            <IconButton
              className={classes.standardButton}
              onClick={() => {
                setNewTicketModalOpen(true);
              }}
              aria-label={i18n.t("tickets.inbox.newTicket")}
            >
              <AddIcon className={classes.standardIcon} />
            </IconButton>
          </Tooltip>
          {user.profile === "admin" && (
            <Tooltip placement="top" title={i18n.t("tickets.inbox.closedAll")}>
              <IconButton
                className={classes.standardButton}
                onClick={handleSnackbarOpen}
                aria-label={i18n.t("tickets.inbox.closedAll")}
              >
                <PlaylistAddCheckOutlined className={classes.standardIcon} />
              </IconButton>
            </Tooltip>
          )}
          {/* Botão "Abertos" */}
          <Tooltip placement="top" title={i18n.t("tickets.inbox.open")}>
            <IconButton
              className={clsx(classes.standardButton, {
                [classes.activeButton]: tab === "open",
              })}
              onClick={() => handleChangeTab(null, "open")}
            >
              <MoveToInboxIcon
                className={clsx(classes.standardIcon, {
                  [classes.activeIcon]: tab === "open",
                })}
              />
            </IconButton>
          </Tooltip>
          {/* Botão "Resolvidos" */}
          <Tooltip placement="top" title={i18n.t("tickets.inbox.resolverd")}>
            <IconButton
              className={clsx(classes.standardButton, {
                [classes.activeButton]: tab === "closed",
              })}
              onClick={() => handleChangeTab(null, "closed")}
            >
              <CheckBoxIcon
                className={clsx(classes.standardIcon, {
                  [classes.activeIcon]: tab === "closed",
                })}
              />
            </IconButton>
          </Tooltip>
          {tab !== "closed" && tab !== "search" && (
            <Tooltip placement="top" title={!sortTickets ? "Crescente" : "Decrescente"}>
              <ToggleButton
                className={clsx(classes.standardButton, {
                  [classes.activeButton]: sortTickets,
                })}
                value="uncheck"
                selected={sortTickets}
                onChange={() => setSortTickets((prevState) => !prevState)}
              >
                {!sortTickets ? (
                  <TextRotateUp className={classes.standardIcon} />
                ) : (
                  <TextRotationDown className={clsx(classes.standardIcon, classes.activeIcon)} />
                )}
              </ToggleButton>
            </Tooltip>
          )}
          {/* Seletor de filas à direita, como pill (mockup) */}
          <div className={classes.queueSelectWrapper}>
            <TicketsQueueSelect
              selectedQueueIds={selectedQueueIds}
              userQueues={user?.queues}
              onChange={(values) => setSelectedQueueIds(values)}
            />
          </div>
        </div>
      </div>

      {filter === true && (
        <>
          <Tooltip placement="top" title="Marque para pesquisar também nos conteúdos das mensagens (mais lento)">
            <div className={classes.searchOnMessagesRow}>
              <Switch
                size="small"
                checked={searchOnMessages}
                onChange={(e) => { setSearchOnMessages(e.target.checked) }}
              />
              <span>Buscar dentro das mensagens</span>
            </div>
          </Tooltip>
          <TagsFilter onFiltered={handleSelectedTags} />
          <WhatsappsFilter onFiltered={handleSelectedWhatsapps} />
          <StatusFilter onFiltered={handleSelectedStatus} />
          {profile === "admin" && (
            <>
              <UsersFilter onFiltered={handleSelectedUsers} />
            </>
          )}
        </>
      )}

      <TabPanel value={tab} name="open" className={classes.ticketsWrapper}>
        <Tabs
          value={tabOpen}
          onChange={handleChangeTabOpen}
          variant="fullWidth"
          className={classes.aerosTabs}
          TabIndicatorProps={{ style: { display: "none" } }}
        >
          {/* ATENDENDO */}
          <Tab
            label={
              <span>
                {i18n.t("ticketsList.assignedHeader")}
                <span className={clsx(classes.countPill, classes.countPillActive)}>
                  {openCount}
                </span>
              </span>
            }
            value={"open"}
            name="open"
            classes={{ root: classes.aerosTab }}
          />

          {/* AGUARDANDO */}
          <Tab
            label={
              <span>
                {i18n.t("ticketsList.pendingHeader")}
                <span className={clsx(classes.countPill, classes.countPillPending)}>
                  {pendingCount}
                </span>
              </span>
            }
            value={"pending"}
            name="pending"
            classes={{ root: classes.aerosTab }}
          />

          {/* GRUPOS */}
          {user.allowGroup && (
            <Tab
              label={
                <span>
                  {i18n.t("ticketsList.groupingHeader")}
                  <span className={clsx(classes.countPill, classes.countPillNeutral)}>
                    {groupingCount}
                  </span>
                </span>
              }
              value={"group"}
              name="group"
              classes={{ root: classes.aerosTab }}
            />
          )}
        </Tabs>

        <Paper className={classes.ticketsWrapper}>
          <TicketsList
            status="open"
            showAll={showAllTickets}
            sortTickets={sortTickets ? "ASC" : "DESC"}
            selectedQueueIds={selectedQueueIds}
            updateCount={(val) => setOpenCount(val)}
            style={applyPanelStyle("open")}
            setTabOpen={setTabOpen}
          />
          <TicketsList
            status="pending"
            selectedQueueIds={selectedQueueIds}
            sortTickets={sortTickets ? "ASC" : "DESC"}
            showAll={user.profile === "admin" || user.allUserChat === 'enabled' ? showAllTickets : false}
            updateCount={(val) => setPendingCount(val)}
            style={applyPanelStyle("pending")}
            setTabOpen={setTabOpen}
          />
          {user.allowGroup && (
            <TicketsList
              status="group"
              showAll={showAllTickets}
              sortTickets={sortTickets ? "ASC" : "DESC"}
              selectedQueueIds={selectedQueueIds}
              updateCount={(val) => setGroupingCount(val)}
              style={applyPanelStyle("group")}
              setTabOpen={setTabOpen}
            />
          )}
        </Paper>
      </TabPanel>

      <TabPanel value={tab} name="closed" className={classes.ticketsWrapper}>
        <TicketsList
          status="closed"
          showAll={showAllTickets}
          selectedQueueIds={selectedQueueIds}
          setTabOpen={setTabOpen}
        />
      </TabPanel>

      <TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
        {profile === "admin" && (
          <>
            <TicketsList
              statusFilter={selectedStatus}
              searchParam={searchParam}
              showAll={showAllTickets}
              tags={selectedTags}
              users={selectedUsers}
              selectedQueueIds={selectedQueueIds}
              whatsappIds={selectedWhatsapp}
              forceSearch={forceSearch}
              searchOnMessages={searchOnMessages}
              status="search"
            />
          </>
        )}

        {profile === "user" && (
          <TicketsList
            statusFilter={selectedStatus}
            searchParam={searchParam}
            showAll={false}
            tags={selectedTags}
            selectedQueueIds={selectedQueueIds}
            whatsappIds={selectedWhatsapp}
            forceSearch={forceSearch}
            searchOnMessages={searchOnMessages}
            status="search"
          />
        )}
      </TabPanel>
    </Paper >
  );
};

export default TicketsManagerTabs;